import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { formatMoneyFromUnknown, resolveCurrencyCode } from '@/lib/money'
import { useOrder } from '../../context/OrderContext'
import { Card, CardHeader } from '../../components/ui/Card'
import { SkeletonDetailFields, SkeletonTable } from '../../components/ui/Skeleton'
import { Button } from '../../components/ui/Button'
import { ConfirmModal } from '../../components/ui/ConfirmModal'
import type { StoreOrderLineDto } from '../../services/orderService'
import { formatStandardAddress } from '@/lib/formatStandardAddress'

/** Main fulfillment progression (matches API). Only forward moves (or cancel before shipped). */
const FULFILLMENT_STATUS_ORDER = [
  'pending_payment',
  'processing',
  'pending',
  'confirmed',
  'preparing',
  'shipped',
  'delivered',
] as const

function statusRank(status: string): number {
  const i = FULFILLMENT_STATUS_ORDER.indexOf(status as (typeof FULFILLMENT_STATUS_ORDER)[number])
  return i
}

/** Selectable statuses: current and later pipeline steps; optional cancel before shipment. */
function selectableStatuses(current: string | undefined): string[] {
  const s = String(current ?? '').trim()
  const rank = statusRank(s)
  if (s === 'cancelled') {
    return ['cancelled']
  }
  if (rank < 0) {
    return [...FULFILLMENT_STATUS_ORDER, 'cancelled']
  }
  const forward = FULFILLMENT_STATUS_ORDER.slice(rank)
  const out: string[] = [...forward]
  if (rank < statusRank('shipped')) {
    out.push('cancelled')
  }
  return out
}

function humanizeStatus(status: string): string {
  return status
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function offerLabelFromMeta(meta: unknown): string | null {
  if (!meta || typeof meta !== 'object') return null
  const m = meta as { offer?: { title?: string } | null }
  const t = m.offer?.title?.trim()
  return t || null
}

function paymentSummary(order: { paymentMethod?: string; paymentStatus: string; deliveryType?: string }): string {
  if (order.paymentMethod === 'pay_on_delivery') {
    if (order.deliveryType === 'pickup') {
      return 'Pay at pickup — customer pays when they collect the order (not credited to your platform wallet).'
    }
    return 'Pay on delivery — customer pays when they receive the order (not credited to your platform wallet).'
  }
  if (order.paymentMethod === 'online') {
    return `Online (${order.paymentStatus || 'pending'})`
  }
  return order.paymentStatus || '—'
}

function lineDisplayName(line: StoreOrderLineDto): string {
  const name = line.product?.name?.trim()
  if (name) return name
  const slug = line.product?.slug?.trim()
  if (slug) return slug
  const meta = line.pricingMetadata as { productSlug?: string } | null
  if (meta && typeof meta.productSlug === 'string' && meta.productSlug.trim()) {
    return meta.productSlug
  }
  return `Product #${line.productId}`
}

export default function OrderDetail() {
  const { orderRef } = useParams()
  const {
    selectedOrder,
    getOrderByReference,
    updateOrderStatus,
    mutationLoading,
    error,
    clearError,
  } = useOrder()
  const [status, setStatus] = useState('pending')
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false)

  useEffect(() => {
    const ref = orderRef?.trim()
    if (!ref) return
    clearError()
    getOrderByReference(ref).catch(() => {})
  }, [orderRef, getOrderByReference, clearError])

  useEffect(() => {
    if (
      selectedOrder?.orderReference === orderRef?.trim() &&
      selectedOrder.status &&
      typeof selectedOrder.status === 'string'
    ) {
      setStatus(selectedOrder.status)
    }
  }, [selectedOrder?.orderReference, selectedOrder?.status, orderRef])

  const statusChoices = selectedOrder?.status
    ? selectableStatuses(selectedOrder.status)
    : [...FULFILLMENT_STATUS_ORDER, 'cancelled']

  const statusChanged = selectedOrder && status !== selectedOrder.status

  function requestStatusUpdate() {
    if (!selectedOrder || !statusChanged) return
    if (!statusChoices.includes(status)) return
    setStatusConfirmOpen(true)
  }

  async function confirmStatusUpdate() {
    const ref = orderRef?.trim()
    if (!ref || !selectedOrder) return
    clearError()
    await updateOrderStatus(ref, status)
    setStatusConfirmOpen(false)
  }

  const currency = resolveCurrencyCode(selectedOrder?.currency)

  if (!orderRef?.trim()) {
    return (
      <Card>
        <CardHeader title="Order details" description="Review order and update its status." />
        <p className="text-sm text-destructive">Missing order reference in URL.</p>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader title="Order details" description="Review order and update its status." />
      {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}
      {!selectedOrder || selectedOrder.orderReference !== orderRef.trim() ? (
        <div className="space-y-6" aria-busy="true">
          <SkeletonDetailFields fields={6} />
          <SkeletonTable rows={4} columns={5} />
        </div>
      ) : (
        <div className="space-y-8">
          <section>
            <h3 className="text-sm font-semibold text-foreground mb-3">Summary</h3>
            <dl className="grid gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-xs uppercase text-muted-foreground">Order reference</dt>
                <dd className="text-sm font-medium text-foreground font-mono">
                  {selectedOrder.orderReference}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-muted-foreground">Placed</dt>
                <dd className="text-sm font-medium text-foreground">
                  {selectedOrder.createdAt
                    ? new Date(selectedOrder.createdAt).toLocaleString()
                    : '—'}
                </dd>
              </div>
              {selectedOrder.store ? (
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">Store</dt>
                  <dd className="text-sm font-medium text-foreground">
                    {selectedOrder.store.name}
                    <span className="text-muted-foreground"> · {selectedOrder.store.subdomain}</span>
                  </dd>
                </div>
              ) : null}
              {selectedOrder.providerCheckoutReference ? (
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">Payment reference</dt>
                  <dd className="text-sm font-medium text-foreground font-mono break-all">
                    {selectedOrder.providerCheckoutReference}
                  </dd>
                </div>
              ) : null}
              <div>
                <dt className="text-xs uppercase text-muted-foreground">Current status</dt>
                <dd className="text-sm font-medium text-foreground">{selectedOrder.status || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-muted-foreground">Payment</dt>
                <dd className="text-sm font-medium text-foreground">{paymentSummary(selectedOrder)}</dd>
              </div>
            </dl>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-foreground mb-3">Customer</h3>
            <dl className="grid gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-xs uppercase text-muted-foreground">Name</dt>
                <dd className="text-sm font-medium text-foreground">
                  {selectedOrder.customerName || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-muted-foreground">Email</dt>
                <dd className="text-sm font-medium text-foreground">
                  {selectedOrder.customer?.email || '—'}
                </dd>
              </div>
            </dl>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-foreground mb-3">Fulfillment</h3>
            <p className="text-sm text-foreground capitalize mb-2">
              Type: <strong>{selectedOrder.deliveryType || '—'}</strong>
            </p>
            {selectedOrder.deliveryNotes ? (
              <p className="text-sm text-muted-foreground mb-3">
                Order notes: {selectedOrder.deliveryNotes}
              </p>
            ) : null}
            {selectedOrder.deliveryType === 'delivery' && selectedOrder.customerAddress ? (
              <div className="rounded-xl border border-border bg-muted/20 p-4 text-sm">
                <p className="font-medium text-foreground">{selectedOrder.customerAddress.name}</p>
                <p className="text-muted-foreground mt-1">{selectedOrder.customerAddress.phone}</p>
                <p className="mt-2 text-foreground">
                  {formatStandardAddress(selectedOrder.customerAddress)}
                </p>
                {selectedOrder.customerAddress.deliveryNotes ? (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Address notes: {selectedOrder.customerAddress.deliveryNotes}
                  </p>
                ) : null}
              </div>
            ) : selectedOrder.deliveryType === 'pickup' ? (
              selectedOrder.pickupLocation ? (
                <div className="rounded-xl border border-border bg-muted/20 p-4 text-sm">
                  <p className="font-medium text-foreground">{selectedOrder.pickupLocation.name}</p>
                  <p className="mt-2 text-foreground whitespace-pre-wrap">
                    {selectedOrder.pickupLocation.addressText}
                  </p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Pickup fee (charged at checkout): {selectedOrder.pickupLocation.feeAmount}{' '}
                    {selectedOrder.currency}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Customer pickup — no location on file.</p>
              )
            ) : null}
          </section>

          <section>
            <h3 className="text-sm font-semibold text-foreground mb-3">Line items</h3>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="min-w-full divide-y divide-border text-sm">
                <thead className="bg-muted/30 text-left text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">S/N</th>
                    <th className="px-4 py-3 font-medium">Product</th>
                    <th className="px-4 py-3 font-medium">Qty</th>
                    <th className="px-4 py-3 font-medium tabular-nums">List / unit</th>
                    <th className="px-4 py-3 font-medium tabular-nums">Sale / unit</th>
                    <th className="px-4 py-3 font-medium tabular-nums">Line total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-card">
                  {(selectedOrder.lines || []).map((line, rowIndex) => {
                    const listP = Number(line.unitListPrice)
                    const saleP = Number(line.unitSalePrice)
                    const offerLabel = offerLabelFromMeta(line.pricingMetadata)
                    return (
                      <tr key={line.id}>
                        <td className="px-4 py-3 tabular-nums">{rowIndex + 1}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-foreground">{lineDisplayName(line)}</div>
                          {line.product?.sku ? (
                            <div className="text-xs text-muted-foreground">SKU {line.product.sku}</div>
                          ) : null}
                          {offerLabel ? (
                            <div className="text-xs text-primary mt-0.5">Offer: {offerLabel}</div>
                          ) : null}
                          {!offerLabel && listP > saleP ? (
                            <div className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                              Discounted
                            </div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 tabular-nums">{line.quantity}</td>
                        <td className="px-4 py-3 tabular-nums">
                          {formatMoneyFromUnknown(line.unitListPrice, currency)}
                        </td>
                        <td className="px-4 py-3 tabular-nums">
                          {formatMoneyFromUnknown(line.unitSalePrice, currency)}
                        </td>
                        <td className="px-4 py-3 tabular-nums font-medium">
                          {formatMoneyFromUnknown(line.lineTotal, currency)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              {(!selectedOrder.lines || selectedOrder.lines.length === 0) && (
                <p className="px-4 py-6 text-center text-muted-foreground text-sm">
                  No line items.
                </p>
              )}
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-foreground mb-3">Totals</h3>
            <div className="max-w-sm space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="tabular-nums font-medium">
                  {formatMoneyFromUnknown(selectedOrder.subtotal, currency)}
                </span>
              </div>
              {selectedOrder.discountCode &&
              Number(selectedOrder.discountTotal ?? 0) > 0 ? (
                <div className="flex justify-between gap-4 text-emerald-700 dark:text-emerald-400">
                  <span>Promo ({selectedOrder.discountCode})</span>
                  <span className="tabular-nums font-medium">
                    −{formatMoneyFromUnknown(selectedOrder.discountTotal, currency)}
                  </span>
                </div>
              ) : null}
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">
                  Tax
                  {selectedOrder.taxRateAppliedPercent != null &&
                  String(selectedOrder.taxRateAppliedPercent).trim() !== '' &&
                  Number(selectedOrder.taxRateAppliedPercent) > 0
                    ? ` (${selectedOrder.taxRateAppliedPercent}%)`
                    : ''}
                </span>
                <span className="tabular-nums font-medium">
                  {formatMoneyFromUnknown(selectedOrder.taxTotal, currency)}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Shipping</span>
                <span className="tabular-nums font-medium">
                  {formatMoneyFromUnknown(selectedOrder.shippingTotal, currency)}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Fees</span>
                <span className="tabular-nums font-medium">
                  {formatMoneyFromUnknown(selectedOrder.feeTotal, currency)}
                </span>
              </div>
              <div className="flex justify-between gap-4 pt-2 border-t border-border font-semibold">
                <span>Total</span>
                <span className="tabular-nums">
                  {formatMoneyFromUnknown(selectedOrder.totalAmount, currency)}
                </span>
              </div>
            </div>
          </section>

          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <select
                className="rounded-xl border border-border px-3 py-2 text-sm bg-card min-w-[14rem]"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {statusChoices.map((option) => (
                  <option key={option} value={option}>
                    {humanizeStatus(option)}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                onClick={requestStatusUpdate}
                disabled={mutationLoading || !statusChanged || !statusChoices.includes(status)}
              >
                {mutationLoading ? 'Updating...' : 'Update status'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground max-w-xl">
              Status can only move forward in the fulfillment flow (or be set to cancelled before an order is
              shipped). Earlier steps cannot be selected again.
            </p>
          </div>
        </div>
      )}
      <ConfirmModal
        open={statusConfirmOpen}
        title="Update order status?"
        size="md"
        confirmLabel="Yes, update and notify customer"
        cancelLabel="Cancel"
        variant="primary"
        onClose={() => setStatusConfirmOpen(false)}
        onConfirm={confirmStatusUpdate}
        description={
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              The order status will change to{' '}
              <strong className="text-foreground">{humanizeStatus(status)}</strong>. The customer will receive an
              email with the updated information about this order.
            </p>
            <p>Are you sure you want to proceed?</p>
          </div>
        }
      />
    </Card>
  )
}
